import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTeamView } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getPriorityBadgeClass, getStatusBadgeClass, formatApiError, isOverdue, getInitials } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { UsersThree, CalendarCheck, ClipboardText, Warning, Clock, Check } from '@phosphor-icons/react';

export default function TeamViewPage() {
  const { user, isAdmin, isManager } = useAuth();
  const [viewType, setViewType] = useState('follow-ups');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [data, setData] = useState({ items: [], team_members: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { view_type: viewType };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      const res = await getTeamView(params);
      setData(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewType, selectedEmployee]);

  if (!isAdmin && !isManager) {
    return (
      <div className="p-6 text-center">
        <UsersThree size={48} className="mx-auto text-zinc-300 mb-4" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-zinc-500 mt-2">Only managers and admins can view team data.</p>
      </div>
    );
  }

  // Calculate totals from summary
  const totalPending = Object.values(data.summary).reduce((sum, s) => sum + (s.pending || 0), 0);
  const totalOverdue = Object.values(data.summary).reduce((sum, s) => sum + (s.overdue || 0), 0);
  const totalToday = Object.values(data.summary).reduce((sum, s) => sum + (s.today || 0), 0);
  const totalCompleted = Object.values(data.summary).reduce((sum, s) => sum + (s.completed || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="team-view-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Team View
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Monitor your team's follow-ups and tasks
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-50 rounded-sm">
              <Clock size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums">{totalToday}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-500">Due Today</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 rounded-sm bg-red-50/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-sm">
              <Warning size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums text-red-600">{totalOverdue}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-500">Overdue</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-sm">
              <ClipboardText size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums">{totalPending}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-500">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-sm">
              <Check size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums">{totalCompleted}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-zinc-500">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Tabs value={viewType} onValueChange={setViewType} className="flex-1">
          <TabsList className="bg-zinc-100 p-1 rounded-sm">
            <TabsTrigger value="follow-ups" className="rounded-sm text-xs uppercase tracking-wider">
              <CalendarCheck size={14} className="mr-1" /> Follow-ups
            </TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-sm text-xs uppercase tracking-wider">
              <ClipboardText size={14} className="mr-1" /> Tasks
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <SearchableSelect
          value={selectedEmployee}
          onValueChange={setSelectedEmployee}
          options={[{ value: 'all', label: 'All Team Members' }, ...data.team_members.map(m => ({ value: m.id, label: m.name }))]}
          placeholder="All Team Members"
          triggerClassName="w-[200px]"
          data-testid="employee-filter"
        />
      </div>

      {/* Team Members Summary */}
      <Card className="border-zinc-200 rounded-sm">
        <CardHeader className="border-b border-zinc-100">
          <CardTitle className="text-base flex items-center gap-2">
            <UsersThree size={18} className="text-[#002FA7]" />
            Team Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : data.team_members.length === 0 ? (
            <div className="p-6 text-center text-zinc-500">
              No team members found
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {data.team_members.map(member => {
                const stats = data.summary[member.id] || {};
                return (
                  <div
                    key={member.id}
                    className={`p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors cursor-pointer ${selectedEmployee === member.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedEmployee(selectedEmployee === member.id ? 'all' : member.id)}
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-[#002FA7] text-white text-sm">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{member.name}</p>
                        <p className="text-xs text-zinc-500">{member.designation || (member.roles?.length ? member.roles.map(r => r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r).join(', ') : member.role)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold tabular-nums text-amber-600">{stats.today || 0}</p>
                        <p className="text-xs text-zinc-500">Today</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold tabular-nums text-red-600">{stats.overdue || 0}</p>
                        <p className="text-xs text-zinc-500">Overdue</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold tabular-nums">{stats.pending || 0}</p>
                        <p className="text-xs text-zinc-500">Pending</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold tabular-nums text-green-600">{stats.completed || 0}</p>
                        <p className="text-xs text-zinc-500">Done</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items List */}
      <Card className="border-zinc-200 rounded-sm">
        <CardHeader className="border-b border-zinc-100">
          <CardTitle className="text-base">
            {viewType === 'follow-ups' ? 'Follow-ups' : 'Tasks'}
            {selectedEmployee !== 'all' && ` - ${data.team_members.find(m => m.id === selectedEmployee)?.name || ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : data.items.length === 0 ? (
            <div className="p-6 text-center text-zinc-500">
              No {viewType} found
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {data.items.map(item => {
                const isOverdueItem = item.status !== 'Completed' && isOverdue(item.due_date);
                return (
                  <div
                    key={item.id}
                    className={`p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors ${isOverdueItem ? 'bg-red-50/50' : ''}`}
                    data-testid={`item-${item.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-sm">
                          {viewType === 'follow-ups' ? item.follow_up_type : item.title}
                        </p>
                        <Badge className={`${getPriorityBadgeClass(item.priority)} text-xs`}>{item.priority}</Badge>
                        <Badge className={`${getStatusBadgeClass(item.status)} text-xs`}>{item.status}</Badge>
                        {isOverdueItem && <Badge className="badge-overdue text-xs">Overdue</Badge>}
                      </div>
                      <p className="text-sm text-zinc-600 mt-1">
                        <Link to={`/clients/${item.client_id}`} className="hover:text-[#002FA7]">
                          {item.client_name || 'Unknown Client'}
                        </Link>
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Due: {formatDate(item.due_date)} {item.due_time || ''} • {item.assigned_to_name || 'Unassigned'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
